import type { AxiosResponse } from 'axios'
import type { components } from 'schema.ts'

import { getFilenameFromHeaders } from '~/portal/api/api.helpers.ts'
import type { IFetchApiProps } from '~/portal/api/axios.ts'
import { FETCH_API_METHODS, fetchApi } from '~/portal/api/axios.ts'
import { STATE_STORE_KEYS } from '~/portal/types'

export interface IFetchApiControllerList {
	token: IFetchApiProps['token']
	projectId?: number
	params?: {
		page?: string | number
		page_size?: string | number
		search?: string | number
		type_id?: string | number
		return_all?: boolean
	}
}

export interface IFetchApiExportController {
	token: IFetchApiProps['token']
	id: number
}

export interface IFetchApiExportControllerTemplate {
	token: IFetchApiProps['token']
}

export interface IFetchApiFindController {
	token: IFetchApiProps['token']
	projectId: number
	payload: components['schemas']['FindProjectController']
}

export interface IFetchDeleteController {
	token: IFetchApiProps['token']
	projectId: number
	controllerId: number
}

export const fetchApiControllerList = async (
	payload: IFetchApiControllerList
) => {
	const url = payload.projectId
		? `/project/${payload.projectId}/controller/list`
		: '/product/controller/list'

	return await fetchApi({
		url,
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ControllerList,
		params: {
			...payload.params
		}
	})
}

export interface IFetchControllerTypeList {
	token: IFetchApiProps['token']
	params?: {
		page?: string | number
		return_all?: boolean
		search?: string
		type?: string
		type_id?: number
	}
}

export interface IFetchApiDeleteControllerDB {
	token: IFetchApiProps['token']
	id: number
}

export const fetchControllerTypeList = async (
	payload: IFetchControllerTypeList
) => {
	return await fetchApi({
		url: '/product/type/list',
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ControllerTypeList,
		params: {
			...payload.params,
			return_all: true
		}
	})
}

export const fetchFindController = ({
	token,
	projectId,
	payload
}: IFetchApiFindController) => {
	return fetchApi({
		token,
		method: FETCH_API_METHODS.Post,
		pendingKey: STATE_STORE_KEYS.ControllerFind,
		url: `/project/${projectId}/controller/find`,
		payload
	})
}

export const fetchDeleteController = ({
	token,
	projectId,
	controllerId
}: IFetchDeleteController) => {
	return fetchApi({
		token,
		method: FETCH_API_METHODS.Delete,
		pendingKey: STATE_STORE_KEYS.ControllerDelete,
		url: `/project/${projectId}/controller/${controllerId}/delete`
	})
}

export const fetchApiDeleteControllerDB = async ({
	token,
	id
}: IFetchApiDeleteControllerDB) => {
	return fetchApi({
		url: `/product/controller/${id}/delete`,
		method: FETCH_API_METHODS.Delete,
		token,
		pendingKey: STATE_STORE_KEYS.ControllerDelete
	})
}

export const fetchApiExportController = async ({
	token,
	id
}: IFetchApiExportController): Promise<{
	blob: Blob
	filename: string | null
}> => {
	const url = `/product/controller/${id}/export`
	const resp = (await fetchApi({
		url,
		method: FETCH_API_METHODS.Get,
		token,
		pendingKey: STATE_STORE_KEYS.ControllerExport,
		responseType: 'blob'
	})) as AxiosResponse<Blob>

	const filename = getFilenameFromHeaders(
		resp.headers,
		url,
		`controller-${id}.xlsx`
	)
	return { blob: resp.data, filename }
}

export const fetchApiExportControllerTemplate = async ({
	token
}: IFetchApiExportControllerTemplate): Promise<{
	blob: Blob
	filename: string | null
}> => {
	const url = '/product/controller/export/template'
	const resp = (await fetchApi({
		url,
		method: FETCH_API_METHODS.Get,
		token,
		pendingKey: STATE_STORE_KEYS.ControllerExportAll,
		responseType: 'blob'
	})) as AxiosResponse<Blob>

	const filename = getFilenameFromHeaders(
		resp.headers,
		url,
		'controllers-template.xlsx'
	)
	return { blob: resp.data, filename }
}
